import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Button,
  H1,
  Paragraph,
  Table,
  THead,
  Td,
  Th,
  Modal,
  ModalHeader,
  ModalContent,
  Spinner,
  Caption,
  TableRow,
  InputSearch,
  Grid,
  TableWrapper
} from '@increasecard/typed-components';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useShallowEqualSelector } from '../../utils/reduxUtils';
import { ManagerUsersActions } from './actions/managerUsersActions';
import { ManagerUser } from '@card/types';

const ModalShopName = styled.div`
  padding: 8px 0;
  border-top: 1px solid ${({ theme }) => theme.colorsNew.gray.grayLight};
`;

const ButtonMore = styled.button`
  color: blue;
`;

const RowLabel = styled.div`
  ${({ theme }) => theme.typography.inputLabel}
  font-weight: 700;
  color: ${({ theme }) => theme.colorsNew.gray.grayBold};
`;

export function UsersScreen() {
  const { managerUsersFiltered, searchWord, loadingManagerUsers } = useShallowEqualSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state) => state.managerUsers
  );
  const [showShopList, setShowShopList] = useState(false);
  const [currentUserSelected, setCurrentUserSelected] = useState<ManagerUser | undefined>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(ManagerUsersActions.getManagerUsers());
  }, [dispatch]);

  const selectUser = (user: ManagerUser) => {
    setCurrentUserSelected(user);
  };

  const toggleShowShopList = () => {
    setShowShopList(!showShopList);
  };

  const setCurrentUserToEdit = (currentUser: ManagerUser) =>
    dispatch(ManagerUsersActions.setCurrentUserToEdit(currentUser));

  const filterUsers = (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch(ManagerUsersActions.filterUsers(e.target.value));

  const newUser = () => {
    const url = process.env.REACT_APP_PLATFORM_NEW_USER_URL;
    if (url) {
      window.location.href = url;
    }
  };

  return (
    <Grid rowGap={2}>
      <div>
        <H1>{t(`abm:Users:title`)}</H1>
        <Paragraph>{t(`abm:Users:description`)}</Paragraph>
      </div>
      <div className="grid-span-desktop-4">
        <InputSearch
          id="search"
          label={t(`abm:Users:searchPlaceholder`)}
          labelHidden
          onChange={filterUsers}
          placeholder={t(`abm:Users:searchPlaceholder`)}
          style={{ width: '300px' }}
          value={searchWord}
        />
      </div>
      <div className="grid-span-desktop-6 grid-start-desktop-7" style={{ textAlign: 'right' }}>
        <Button buttonType="invisible" onClick={newUser}>
          {t(`abm:Users:inviteNewUser`)}
        </Button>
      </div>
      <TableWrapper>
        <Table hasData={loadingManagerUsers || managerUsersFiltered.length !== 0} zebra>
          <THead>
            <TableRow double>
              <Th>{t(`abm:Users:table:header:user`)}</Th>
              <Th>{t(`abm:Users:table:header:role`)}</Th>
              <Th>{t(`abm:Users:table:header:status`)}</Th>
              <Th>{t(`abm:Users:table:header:shops`)}</Th>
              <Th />
            </TableRow>
          </THead>
          <tbody>
            {loadingManagerUsers ? (
              <TableRow double>
                <Td colSpan={5} style={{ textAlign: 'center' }}>
                  <Spinner size={36} />
                </Td>
              </TableRow>
            ) : (
              managerUsersFiltered.map((managerUser) => {
                return (
                  <TableRow double key={managerUser.email}>
                    <Td>
                      <div>
                        <strong>
                          {managerUser.first_name} {managerUser.last_name}
                        </strong>
                      </div>
                      {managerUser.email}
                    </Td>
                    <Td>
                      <RowLabel>Rol</RowLabel>
                      {managerUser.account_administrator ? (
                        <p>{t(`abm:Users:table:body:role_admin`)}</p>
                      ) : (
                        <p>{t(`abm:Users:table:body:role_colab`)}</p>
                      )}
                    </Td>
                    <Td>
                      <RowLabel>
                        {managerUser.validated_account
                          ? t(`abm:Users:table:body:invitation_accepted`)
                          : t(`abm:Users:table:body:invitation_pending`)}
                      </RowLabel>
                      {/* TODO(santi698): Add accepted_date when available */}
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', justifyContent: 'baseline' }}>
                        {managerUser.shops.length === 0
                          ? t(`abm:Users:table:body:no_shops`)
                          : managerUser.shops[0].name}
                        {managerUser.shops.length > 1 && (
                          <ButtonMore
                            onClick={() => {
                              selectUser(managerUser);
                              toggleShowShopList();
                            }}
                            style={{ marginLeft: '0.5ex' }}
                          >
                            {t('abm:Users:table:body:more_shops', {
                              shopsCount: managerUser.shops.length - 1
                            })}
                          </ButtonMore>
                        )}
                      </div>
                    </Td>
                    <Td rightAligned>
                      {managerUser.account_administrator && (
                        <Link to={`./users/edit/${managerUser.id}`}>
                          <Button
                            buttonType="outline"
                            onClick={() => setCurrentUserToEdit(managerUser)}
                            size="small"
                          >
                            <p>{t(`abm:Users:table:body:edit`)}</p>
                          </Button>
                        </Link>
                      )}
                    </Td>
                  </TableRow>
                );
              })
            )}
          </tbody>
        </Table>
      </TableWrapper>
      {currentUserSelected && (
        <Modal
          onCancel={toggleShowShopList}
          onClose={toggleShowShopList}
          onOk={toggleShowShopList}
          visible={showShopList}
          width="437"
        >
          <ModalHeader align="left">{t(`abm:Users:modal:modal_header`)}</ModalHeader>
          <ModalContent align="left" scrollable>
            <div style={{ marginBottom: '8px' }}>
              <Paragraph weight="bold">
                {currentUserSelected.first_name} {currentUserSelected.last_name}
              </Paragraph>
              <Caption>{currentUserSelected.email}</Caption>
            </div>
            {currentUserSelected.shops &&
              currentUserSelected.shops.map((shop, key) => (
                <ModalShopName key={key}>{shop.name}</ModalShopName>
              ))}
          </ModalContent>
        </Modal>
      )}
    </Grid>
  );
}
